import { Action } from "redux";
import { channel } from "redux-saga";
import {
  all,
  call,
  delay,
  put,
  race,
  take,
  takeLeading,
} from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import { SaleAction } from "actions";

import { SALE } from "constants/actionTypes";

import toast from "lib/toast";

import { fetchDecaySchedule } from "offline/retry";

import { api } from "sagas/api";

export const CONCURRENT_CHUNKS = 8;

type Chunk = Record<string, string | number>;

interface ChunkResult {
  payload: unknown[];
  lastModifiedTimestamp?: string;
}

interface ChunkedActionTypes {
  dataType: string;
  FETCH_CHUNK: { SUCCESS: string; FAILURE: string };
  FETCH_CHUNKED: { REQUEST: string; SUCCESS: string; FAILURE: string };
  FETCH_CHANGES: { SUCCESS: string; FAILURE: string };
}

interface ChunkedActionCreator {
  requestChunk: (chunk: Chunk, chunkId: string, options: object) => Action;
}

interface RequestChunkedAction {
  type: string;
  meta: { metadataUrl: string; options: object };
}

const chunkedFetchables: [ChunkedActionTypes, ChunkedActionCreator][] = [
  [SALE, SaleAction],
];

const dataTypeLabels: Record<string, string> = {
  [SALE.dataType]: "sales",
};

// Chunk requests are redux-offline GETs, so transient failures are already
// retried by the offline queue; a rollback here is final.
function* fetchChunks(
  actionType: ChunkedActionTypes,
  actionCreator: ChunkedActionCreator,
  options: object,
  chunks: [string, Chunk][],
  results: Map<string, ChunkResult>,
  onProgress: () => void,
) {
  let next = 0;
  let failed = false;

  function* worker() {
    while (!failed && next < chunks.length) {
      const [chunkId, chunk] = chunks[next];
      next += 1;
      yield put(actionCreator.requestChunk(chunk, chunkId, options));
      const outcome = yield take(
        outcomeAction =>
          [
            actionType.FETCH_CHUNK.SUCCESS,
            actionType.FETCH_CHUNK.FAILURE,
          ].includes(outcomeAction.type) &&
          outcomeAction.meta?.chunkId === chunkId,
      );
      if (outcome.type === actionType.FETCH_CHUNK.SUCCESS) {
        results.set(chunkId, {
          payload: outcome.payload,
          lastModifiedTimestamp: outcome.meta.lastModifiedTimestamp,
        });
        onProgress();
      } else {
        failed = true;
      }
    }
  }

  yield all(Array.from({ length: CONCURRENT_CHUNKS }, () => call(worker)));
  return !failed;
}

const isTransientError = (error: { code?: number }) =>
  error instanceof TypeError || error?.code >= 500;

// The metadata GET isn't queued offline, so retry it on the queue's schedule.
function* fetchMetadata(metadataUrl: string) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return yield call(api.get, metadataUrl);
    } catch (error) {
      if (attempt >= fetchDecaySchedule.length || !isTransientError(error)) {
        throw error;
      }
      yield delay(fetchDecaySchedule[attempt]);
    }
  }
}

/**
 * Oldest cached time across the chunks: any row in that chunk could have
 * changed since, so the follow-up changesSince starts there.
 */
export const getOldestTimestamp = (
  results: ChunkResult[],
): string | undefined =>
  results
    .map(result => result.lastModifiedTimestamp)
    .filter(Boolean)
    .reduce(
      (oldest, timestamp) =>
        oldest === undefined || parseFloat(timestamp) < parseFloat(oldest)
          ? timestamp
          : oldest,
      undefined,
    );

export function* onRequestChunked(
  actionType: ChunkedActionTypes,
  actionCreator: ChunkedActionCreator,
  action: RequestChunkedAction,
) {
  const { dataType } = actionType;
  const { metadataUrl, options } = action.meta;
  const label = dataTypeLabels[dataType];
  const results = new Map<string, ChunkResult>();
  const runId = uuidv4();
  const toastId = `chunked-fetch-${dataType.toLowerCase()}-${runId}`;
  const cancels = channel<boolean>();
  let chunks: [string, Chunk][] = [];

  const showProgress = () =>
    toast.progress(
      toastId,
      `Fetching ${label}...`,
      chunks.length ? (results.size / chunks.length) * 100 : 0,
      () => cancels.put(true),
    );

  function* fetchAll() {
    try {
      const metadata = yield call(fetchMetadata, metadataUrl);
      chunks = [...metadata.chunks]
        .sort((a: Chunk, b: Chunk) => +a.chunk_start - +b.chunk_start)
        .map((chunk: Chunk, index: number) => [`${runId}-${index}`, chunk]);
      showProgress();
      return yield call(
        fetchChunks,
        actionType,
        actionCreator,
        options,
        chunks,
        results,
        showProgress,
      );
    } catch {
      return false;
    }
  }

  showProgress();
  const { fetched, cancelled } = yield race({
    fetched: call(fetchAll),
    cancelled: take(cancels),
  });
  cancels.close();

  if (!fetched) {
    toast.dismiss(toastId);
    if (!cancelled) {
      toast.error(
        `Couldn't fetch all ${label} at this time. Try again later or contact support.`,
      );
    }
    yield put({
      type: actionType.FETCH_CHUNKED.FAILURE,
      meta: { options, cancelled: Boolean(cancelled) },
    });
    return;
  }

  // Everything's in, so there's nothing left to cancel.
  toast.progress(toastId, `Fetching ${label}...`, 100);

  const chunkResults = chunks.map(([chunkId]) => results.get(chunkId));
  const lastModifiedTimestamp = getOldestTimestamp(chunkResults);

  yield put({
    type: actionType.FETCH_CHUNKED.SUCCESS,
    payload: chunkResults.flatMap(result => result.payload),
    meta: {
      options,
      lastModifiedTimestamp,
      // Triggers the changesSince follow-up (sagas/changesSince).
      cacheHit: lastModifiedTimestamp !== undefined,
    },
  });

  if (lastModifiedTimestamp !== undefined) {
    yield race([
      take(actionType.FETCH_CHANGES.SUCCESS),
      take(actionType.FETCH_CHANGES.FAILURE),
      delay(60000),
    ]);
  }
  toast.dismiss(toastId);
}

export default function* chunkedFetchSaga() {
  yield all(
    chunkedFetchables.map(([actionType, actionCreator]) =>
      takeLeading(
        actionType.FETCH_CHUNKED.REQUEST,
        onRequestChunked,
        actionType,
        actionCreator,
      ),
    ),
  );
}
